import { useEffect, useState } from 'react';
import { Grid, GridCol, TextInput, Checkbox, Button, Group, Title } from '@mantine/core';
import ModalComponent from '../../../components/modal';
import { Language } from '../../../languages/languageHandler';
import { IAct, IEvent, addEvent, updateEvent, deleteEvent } from '../../../../libraries/helpdesk/events/events';
import CustomModal from '../../../components/custom-modal';
import './styles.scss';

interface ModalProps {
  mode: string;
  isOpen: boolean;
  hideBtn: () => void;
  lang: Language;
  event: IEvent;
}

const EditEventManagement = ({ mode, isOpen, hideBtn, lang, event }: ModalProps) => {
  const controller = new AbortController();

  const [loading, setLoading] = useState(false);
  const [eventCode, setEventCode] = useState<number>(event?.eventCode);
  const [description, setDescription] = useState<string>(event?.description);
  const [selectedActs, setSelectedActs] = useState<IAct>(event?.act);

  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (event?.act) {
      setSelectedActs(event.act);
    }
  }, [event]);

  useEffect(() => {
    setEventCode(event?.eventCode);
    setDescription(event?.description);
  }, [event]);

  const toggleAct = (key: keyof IAct) => {
    setSelectedActs(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    const controller = new AbortController();

    try {
      let response;
      if (mode === 'new') {
        response = await addEvent(controller, event.eventCode);
      } else if (mode === 'edit') {
        response = await updateEvent(controller, event.eventCode);
      } else if (mode === 'delete') {
        response = await deleteEvent(controller, event.eventCode);
      }

      if (!response || response.statusOper?.code !== 0) {
        const code = response?.statusOper?.code ?? 'UNKNOWN';
        const message = response?.statusOper?.message ?? 'Unexpected error';

        const baseError =
          mode === 'new'
            ? lang.ADD_EVENT_ERROR || 'Failed to add event'
            : mode === 'edit'
              ? lang.EDIT_EVENT_ERROR || 'Failed to edit event'
              : lang.DELETE_EVENT_ERROR || 'Failed to delete event';

        setErrorMessage(`${baseError} - ${code}, ${message}`);
        setShowErrorDialog(true);

        setTimeout(() => setShowErrorDialog(false), 2000);
      } else {
        const successMsg =
          mode === 'new'
            ? lang.ADD_EVENT_SUCCESS || 'Event successfully added'
            : mode === 'edit'
              ? lang.EDIT_EVENT_SUCCESS || 'Event successfully updated'
              : lang.DELETE_EVENT_SUCCESS || 'Event successfully deleted';

        setSuccessMessage(successMsg);
        setShowSuccessDialog(true);

        setTimeout(() => {
          setShowSuccessDialog(false);
          hideBtn();
        }, 2000);
      }
    } catch (error: any) {
      const baseError =
        mode === 'new'
          ? lang.ADD_EVENT_ERROR || 'Failed to add event'
          : mode === 'edit'
            ? lang.EDIT_EVENT_ERROR || 'Failed to edit event'
            : lang.DELETE_EVENT_ERROR || 'Failed to delete event';

      const code = error?.statusOper?.code || error?.code || 'EXCEPTION';
      const message = error?.statusOper?.message || error?.message || 'An unexpected error occurred';

      setErrorMessage(`${baseError} - ${code}, ${message}`);
      setShowErrorDialog(true);

      setTimeout(() => setShowErrorDialog(false), 2000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <ModalComponent
        show={isOpen}
        handleClick={hideBtn}
        title={`${lang.EVENT_MANAGEMENT} - ${event.eventCode}`}
        content={
          <>
            <Grid>
              <GridCol span={{ base: 12 }}>
                <TextInput
                  label={lang.EVENT_CODE}
                  value={eventCode}
                  onChange={e => setEventCode(Number(e.target.value))}
                  readOnly={mode !== 'new'}
                />
              </GridCol>

              <GridCol span={{ base: 12 }}>
                <TextInput
                  label={lang.EQUIPMENT_DESCRIPTION}
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  readOnly={mode !== 'new'}
                />
              </GridCol>

              {selectedActs && (
                <>
                  <GridCol span={{ base: 12 }}>
                    <Title order={3}>{lang.EVENT_ACTUATION}</Title>
                  </GridCol>

                  {Object.entries(selectedActs).map(([key, value]) => (
                    <GridCol span={{ base: 12 }} key={key}>
                      <Checkbox
                        checked={value}
                        onChange={() => toggleAct(key as keyof IAct)}
                        label={lang[`EVENT_HELPDESK_${key.toUpperCase()}`] || key}
                      />
                    </GridCol>
                  ))}
                </>
              )}

              <Group className="buttons">
                {mode === 'edit' && (
                  <>
                    <Button radius="md" type="button" className="save" onClick={handleSave} loading={loading}>
                      {lang.GLOBAL_SAVE_BUTTON}
                    </Button>
                    <Button
                      type="button"
                      className="delete"
                      onClick={async () => {
                        await deleteEvent(controller);
                        hideBtn();
                      }}
                    >
                      {lang.GLOBAL_DELETE}
                    </Button>
                  </>
                )}

                {mode === 'new' && (
                  <>
                    <Button radius="md" type="button" className="save" onClick={handleSave} loading={loading}>
                      {lang.GLOBAL_SAVE_BUTTON}
                    </Button>
                  </>
                )}
              </Group>
            </Grid>
          </>
        }
      />

      <CustomModal
        opened={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        message={successMessage}
        backgroundColor="#3BDF70"
        textColor="black"
      />

      <CustomModal
        opened={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        message={errorMessage}
        backgroundColor="#E3353F"
        textColor="white"
      />
    </>
  );
};

export default EditEventManagement;
